/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { ContentCard } from '@repo/shared-client/components/blocks'
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useTranslation } from '@web/src/lib/intl/react'
import { StarIcon } from 'lucide-react'
import SFMediaImage from 'src/components/primatives/sf-media-image/sf-media-image'
import { SFReviewHeader } from 'src/components/primatives/sf-review-status/sf-review-status'
import type { NSFWRatingEnum, ReviewEnum } from 'types/graphql'

export interface PublicationCardPublication {
  id: string
  slug?: string | null
  name?: string | null
  author?: string | null
  tagLine?: string | null
  reviewCount?: number | null
  reviewSum?: number | null
  reviewStatus?: ReviewEnum | null
  firstMedia?: {
    imageUrlMedium?: string | null
    reviewStatus?: ReviewEnum | null
    nsfwRating?: NSFWRatingEnum | null
  } | null
  tags?:
    | {
        id: string
        name: string
      }[]
    | null
}

function computeDisplayRating(reviewCount?: number | null, reviewSum?: number | null): string | null {
  if (!reviewCount || reviewCount <= 0) return null
  return Math.min(Math.max(0, (reviewSum ?? 0) / reviewCount), 5.0).toFixed(1)
}

const PublicationImage = ({ publication }: { publication: PublicationCardPublication }) => (
  <div className="relative h-full min-h-[inherit] w-full overflow-hidden bg-black">
    <div
      className="h-full w-full transition-transform duration-300 ease-out group-hover/content-card:scale-[1.06]"
      data-publication-card-media=""
    >
      <SFMediaImage
        backgroundSize="contain"
        blurredBackdrop
        className="h-full w-full"
        media={publication.firstMedia}
        missingImage="/missing-publication.webp"
      />
    </div>
  </div>
)

interface PublicationContentProps {
  publication: PublicationCardPublication
  showRatingInline?: boolean
  collapsed?: boolean
}

const PublicationContent = ({ publication, collapsed }: PublicationContentProps) => {
  const { t } = useTranslation()

  return (
    <div
      className={cn('flex h-full w-full flex-col items-start gap-0 px-2 py-1', collapsed && 'gap-0')}
      data-collapsed={collapsed ? 'true' : undefined}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <Tooltip>
          <TooltipTrigger
            className="w-full text-left"
            render={
              <button
                className="w-full border-none bg-transparent p-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                type="button"
              />
            }
          >
            <span className="overflow-wrap-anywhere line-clamp-2 block w-full font-semibold text-xl tracking-wide">
              {publication.name}
            </span>
          </TooltipTrigger>
          <TooltipContent>{publication.name}</TooltipContent>
        </Tooltip>
      </div>

      {publication.author && (
        <p className="line-clamp-1 w-full text-base text-muted-foreground">
          {t('by {{author}}', { author: publication.author })}
        </p>
      )}

      <div className="flex max-h-[60px] w-full flex-wrap items-start justify-start gap-1 overflow-hidden py-1">
        {publication.tags?.map((tag) => (
          <Badge key={tag.id} variant="outline">
            {tag.name}
          </Badge>
        ))}
      </div>

      <p className="line-clamp-2 pb-1 text-base text-muted-foreground">{publication.tagLine}</p>

      <div className="flex-1" />
    </div>
  )
}

function RatingOverlay({ reviewCount, reviewSum }: { reviewCount?: number | null; reviewSum?: number | null }) {
  const value = computeDisplayRating(reviewCount, reviewSum)
  if (!value) return null

  return (
    <Badge className="absolute top-4 left-4 z-1 bg-black/70" size="default">
      <StarIcon className="text-primary" fill="currentColor" />
      {value}
    </Badge>
  )
}

export type PublicationCardLayoutMode = 'compact' | 'regular'

interface PublicationCardProps {
  cardLayout: PublicationCardLayoutMode
  publication: PublicationCardPublication
}

const PublicationCard = ({ cardLayout, publication }: PublicationCardProps) => {
  const hasMedia = Boolean(publication.firstMedia)
  const isCompact = cardLayout === 'compact'

  const renderBody = (collapsed: boolean) => (
    <PublicationContent collapsed={collapsed} publication={publication} showRatingInline={!hasMedia} />
  )

  const imageNode = <PublicationImage publication={publication} />

  const ratingOverlay = <RatingOverlay reviewCount={publication.reviewCount} reviewSum={publication.reviewSum} />

  return (
    <ContentCard
      bodyClassName="p-1 sm:p-2"
      bodyCollapsed={renderBody(true)}
      bodyExpanded={renderBody(false)}
      compactImageMinHeight={160}
      compactImageWidth={120}
      expandedImageHeight={350}
      header={<SFReviewHeader reviewStatus={publication.reviewStatus} />}
      image={imageNode}
      imageControls={ratingOverlay}
      imageSide="left"
      layout={isCompact ? 'compact' : 'expanded'}
      params={{ publicationIdOrSlug: publication.slug ?? publication.id } as never}
      to="/publication/$publicationIdOrSlug"
    />
  )
}

export default PublicationCard
